import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

import { MAIN_CONTENT_ID } from "./constants/main-content";
import { CopyrightNotice } from "./CopyrightNotice";

interface IMainContentProps extends React.PropsWithChildren {
  isCopyrightLineShown: boolean;
}

export const MainContent = ({
  children,
  isCopyrightLineShown,
}: IMainContentProps) => {
  const { isOpen } = useSidebar();

  return (
    // NOTE: NSAI-7890 — `tabIndex={-1}` lets the skip link move focus here
    // without adding the landmark to the tab order.
    // NOTE: A grid of two rows: the page and, under it, the copyright line.
    // The page's own `h-full` resolves against the first row, so whatever a
    // page draws — its scroll area and that scroll area's scrollbar included —
    // ends above the line. The column is `minmax(0,1fr)` because a grid's
    // implicit column grows to its widest content: a wide table would
    // otherwise stretch the page past the window instead of scrolling inside.
    <main
      id={MAIN_CONTENT_ID}
      tabIndex={-1}
      data-testid="main-content"
      className={cn(
        "grid grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto] grow outline-none transition-all duration-200 h-[calc(100vh-var(--top-navbar-height))] px-0 md:px-4 bg-background",
        isOpen && "md:ps-0",
      )}
    >
      {/* NOTE: NSAI-5822 — the content area is one flat surface. It used to be
          an inset, rounded, differently tinted box sitting on a second tinted
          main element, which stacked three near-identical navies at night; the
          page below paints --background, so this wrapper only owns spacing. */}
      {/* NOTE: NSAI-5822 — vertical padding trimmed to a hairline: the page
          inside adds its own p-5, so 16px here stacked into a visible gap under
          the header and above the bottom edge. Where the copyright line follows
          (from `lg` up), its own 8px top padding is that hairline instead. */}
      {/* NOTE: NSAI-7288 — the content runs to the bottom of the window. A 2rem
          footer bar used to sit under it, holding only a docs link (also in the
          user menu) and the copyright, and it pushed the chat composer up for
          nothing. */}
      {/* NOTE: NSAI-6650 — `Header` insets the breadcrumb by this padding plus
          the pages' own 20px, so the breadcrumb lines up with the page under
          it. Change the horizontal padding here and it changes there too.
          NSAI-6359 — the settings page draws its sidebar hairline back through
          this `px-4`, and through the outer `md:ps-4` as well while the rail
          is collapsed. Change either padding and change those offsets too. */}
      <div
        // NOTE: Where the copyright line follows, it already sets the page off
        // from the window edge, so pages drop their own bottom padding there
        // (`pb-page-end`) instead of stacking a second empty band above it.
        className={cn(
          "h-full min-h-0 px-4 pt-1 pb-2",
          isCopyrightLineShown && "lg:pb-0 lg:[--page-end-padding:0px]",
        )}
      >
        {children}
      </div>
      {/* NOTE: The copyright gets a line of its own under the page instead of
          being overlaid on the page's bottom-right corner. Overlaid, it landed
          on whatever reached that corner — a full-height page's scrollbar, rows
          scrolling under it, a docked side panel. A page that keeps the end of
          its own bottom line free for it (the started chat) claims it through
          `CopyrightLineContext`, and `AppLayout` draws it there instead. Below
          `lg` the account menu carries it.
          It ends on `main`'s own 16px end padding, with an even 8px (`py-2`)
          above and below the text. `justify-self-end` sizes its box to the
          text rather than the row: a full-width box would start at the
          content area's left edge, and anything that reads where the page's
          content begins (the breadcrumb alignment, NSAI-6650) would take
          that empty box for the page's left edge. */}
      {isCopyrightLineShown && (
        <CopyrightNotice
          testId="app-copyright"
          className="hidden justify-self-end whitespace-nowrap py-2 leading-4 text-muted-foreground lg:block"
        />
      )}
    </main>
  );
};
